import { clientLogger } from '@/lib/clientLogger';
/**
 * WebTelnyxVoiceManager — browser (desktop web) counterpart of TelnyxVoiceManager. Exposes the SAME
 * `VoiceBackend` surface so the provider-agnostic `voiceManager` facade delegates to it transparently
 * when running on web with a Telnyx voice provider.
 *
 * Unlike the native manager (which drives a Capacitor plugin over the Telnyx WebRTC iOS/Android SDKs),
 * this uses the browser `@telnyx/webrtc` JS SDK directly — no native plugin, no CallKit/foreground
 * service, no audio-route enum. The SDK client is NOT single-use here (the native "spent client" quirk
 * doesn't apply on web), so one client is reused across calls.
 *
 * Auth + context model is identical to native: log in with the credential JWT from
 * `apiService.getVoiceToken()`, and carry per-call org/company/user/job context in the base64url
 * `X-Client-State` custom SIP header, which the TeXML voice webhook decodes
 * (see DispatchVoip/API/TelnyxWebhookController.ParseClientState).
 *
 * Outbound + RECEIVE MODE: with receive mode enabled (the user is an inbound ring target — see
 * useWebDialerRegistration), this tab holds a web-dialer session (its own Telnyx credential, one
 * per tab) and rings on inbound invites. While receive mode is on, the SESSION token is the SOLE
 * auth — minting a softphone token would reconnect under a different credential and silently
 * deregister this tab. Without receive mode, inbound invites are hung up (legacy behavior).
 * Masked calls are a mobile-only feature and are not supported here.
 */

import { Capacitor } from '@capacitor/core';
import type { TelnyxRTC as TelnyxRTCClient, Call as TelnyxCall, INotification } from '@telnyx/webrtc';
import { apiService } from '@/lib/api';
import type {
  AudioDevice,
  AudioDeviceType,
  CallSummary,
  VoiceCallEvent,
  VoiceEventHandler,
} from '@/lib/twilioVoice';

/** The params of the in-flight call, remembered so an unclean drop can redial the same destination. */
type LastCall = { toE164: string; callerIdE164: string; displayName?: string; jobId?: string };

/**
 * Concurrent calls per tab (call waiting): one talking + one ringing/held. Further invites are
 * auto-rejected exactly as EVERY second invite was before this feature, so the cap is the only
 * thing standing between this and N-way — the roster below is a map, not a pair.
 * ponytail: raising it is a one-line change PLUS a live re-test (audio elements, mic captures and
 * reattach behavior all scale with it) — don't raise it blind.
 */
const MAX_CONCURRENT_CALLS = 5;

/**
 * 'incoming' = ringing, not yet answered (no audio element, no mic).
 * 'active'   = the call whose audio is live.
 * 'held'     = on hold: server-side via telnyx_rtc.modify, PLUS the local layer (see _localHold).
 */
type CallRole = 'incoming' | 'active' | 'held';

/** One live call. The manager keeps a roster of these; `_primaryId` picks the one the flat
 *  VoiceCallEvent describes (so single-call consumers never learn about the others). */
interface ManagedCall {
  /** The SDK call id. NOT stable across a socket reattach — see the re-key in _onNotification. */
  id: string;
  call: TelnyxCall;
  direction: 'inbound' | 'outbound';
  role: CallRole;
  /** Per-call audio sink. The SDK's client-level remoteElement is last-writer-wins across calls
   *  (SDK warning 33011), so concurrency REQUIRES one element per call. Null until answered/dialed. */
  audioEl: HTMLAudioElement | null;
  /** The user's hold intent. Survives socket recovery, which silently returns a held call as
   *  'active' — the intent is what re-asserts the hold (see the 'active' case in _onNotification). */
  intendedHeld: boolean;
  /** Local hold layer applied (mic track muted + audio element muted). */
  localHold: boolean;
  /** The user's MUTE preference for this call — deliberately separate from localHold, so resuming
   *  a call the user had muted brings it back muted rather than live. */
  isMuted: boolean;
  /** Epoch ms of the first 'active' — the UI ticks each call's own duration from this. */
  connectedAt?: number;
  toE164?: string;
  displayName?: string;
  incomingContext?: VoiceCallEvent['incomingContext'];
  /** A server-hold re-assert is in flight (recovery desync); prevents a re-assert loop. */
  reasserting?: boolean;
}

/** Map a browser audio-output device's label to one of the shared route icons (cosmetic only). */
function outputDeviceType(label: string): AudioDeviceType {
  if (/bluetooth|airpod|buds/i.test(label)) return 'bluetooth';
  if (/headphone|headset|wired|earbud/i.test(label)) return 'wired';
  return 'speaker';
}

/**
 * What the org already knows about an inbound invite — which of its numbers is ringing, who is
 * calling, and the job they're probably about — from the X- SIP headers the inbound TeXML puts on
 * each Sip noun (TelnyxWebhookController.BuildSipHeaderParams). The invite's own To: is this tab's
 * credential, so the headers are the only source. Best-effort by design: absent or malformed
 * headers just mean a plainer card, never a broken ring.
 */
function readIncomingContext(call: unknown): VoiceCallEvent['incomingContext'] {
  const headers: Array<{ name?: string; value?: string }> =
    (call as any)?.options?.customHeaders ?? [];
  if (!Array.isArray(headers) || headers.length === 0) return undefined;
  const read = (name: string): string | undefined => {
    const raw = headers.find((h) => h?.name?.toLowerCase() === name)?.value;
    if (!raw) return undefined;
    // The values travel percent-encoded in the SIP URI. Whether they arrive still encoded is the
    // provider's business, so decode tolerantly and keep the raw text if it isn't valid encoding.
    try { return decodeURIComponent(raw); } catch { return raw; }
  };

  const e164 = read('x-did');
  const jobId = read('x-job-id');
  const context: VoiceCallEvent['incomingContext'] = {
    line: e164 ? { e164, name: read('x-did-name'), company: read('x-did-company') } : undefined,
    contactName: read('x-caller-name'),
    job: jobId ? { id: jobId, ref: read('x-job-ref') } : undefined,
  };
  return context.line || context.contactName || context.job ? context : undefined;
}

/** Sentinel for connect/token work invalidated by destroy() — init() must NOT retry these. */
const CONTEXT_CHANGED = 'Voice auth context changed.';

class WebTelnyxVoiceManager {
  private static _instance: WebTelnyxVoiceManager | null = null;
  static get instance(): WebTelnyxVoiceManager {
    if (!WebTelnyxVoiceManager._instance) WebTelnyxVoiceManager._instance = new WebTelnyxVoiceManager();
    return WebTelnyxVoiceManager._instance;
  }

  /** A call that never reaches 'connected' must not hang on "Ringing…" forever. */
  private static readonly CONNECT_TIMEOUT_MS = 45_000;
  /** Delay before an auto-redial, to let things settle after the drop. */
  private static readonly REDIAL_DELAY_MS = 1_500;
  /** Keep the "Call dropped — Redial?" screen up long enough to tap before it settles to idle. */
  private static readonly REDIAL_ENDED_MS = 10_000;
  /** Wait for `telnyx.ready` after connect() before giving up on login. */
  private static readonly LOGIN_TIMEOUT_MS = 10_000;
  /** Session heartbeat cadence — 60s stays within Chrome's 1-minute background-tab timer floor;
   *  the backend liveness window (5min) tolerates several missed beats. */
  private static readonly HEARTBEAT_MS = 60_000;
  /** Telnyx credential propagation: logging in immediately after mint can fail transiently. */
  private static readonly CREDENTIAL_PROPAGATION_MS = 5_000;
  /** Receive-mode reconnect backoff bounds (a registered tab must fight to stay registered). */
  private static readonly RECEIVE_RETRY_BASE_MS = 2_000;
  private static readonly RECEIVE_RETRY_MAX_MS = 60_000;
  /** Retry cadence while the per-user session cap rejects this tab (a slot opens when a tab closes). */
  private static readonly CAP_BLOCK_RETRY_MS = 120_000;
  /**
   * Stop heartbeating after this many CONSECUTIVE failed receive (re)connects (review-6 #5): a
   * tab that cannot reach Telnyx (firewalled rtc.telnyx.com) would otherwise keep its DB session
   * "live" forever — ring-eligible but ringing nowhere, wasting the web hop on every call. With
   * the reconnect backoff (2s→60s), 5 consecutive failures ≈ a solid minute of failing; beats
   * then stop and the session ages out of ring plans within the 5-minute liveness window. A
   * later successful reconnect resets the counter and beats immediately.
   */
  private static readonly SUPPRESS_BEATS_AFTER_FAILURES = 5;
  /**
   * sessionStorage key recording this TAB's live session. sessionStorage is per-tab and survives
   * a refresh/navigation but dies with the tab — exactly the lifetime needed for the successor
   * page to end its predecessor's session (see _reapPredecessorSession). Tab CLOSE still relies
   * on the pagehide beacon; there is no successor page to reap after a close.
   */
  private static readonly SESSION_RECORD_KEY = 'dispatch.webDialerSession';
  /**
   * localStorage key holding sessions of CLOSED tabs awaiting cleanup ({sessionId → endUrl/
   * endToken} map). A closing tab cannot reliably deliver a network request (the pagehide
   * beacon is queued, not confirmed), but a synchronous localStorage write in pagehide is
   * reliable — and localStorage is shared across the origin's tabs, so a surviving tab gets the
   * 'storage' event and ends the session with its full auth context (see _reapOrphanSessions).
   */
  private static readonly ORPHANS_KEY = 'dispatch.webDialerOrphans';

  private _client: TelnyxRTCClient | null = null;
  /** Every live call on this tab, keyed by SDK call id (see MAX_CONCURRENT_CALLS). */
  private _calls = new Map<string, ManagedCall>();
  /** The call the flat VoiceCallEvent describes and the main widget controls. */
  private _primaryId: string | null = null;
  private _initialized = false;
  private _initInFlight: Promise<void> | null = null;
  private _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _connectTimer: ReturnType<typeof setTimeout> | null = null;
  private _redialTimer: ReturnType<typeof setTimeout> | null = null;
  private _listeners: VoiceEventHandler[] = [];
  private _currentState: VoiceCallEvent = { state: 'idle' };
  private _devicechangeBound = false;
  private _selectedSpeakerId = '';

  /** Set by endCall() during 'connecting' (before the INVITE is placed) so the call never rings out. */
  private _abortNextCall = false;
  /**
   * The user hung up this call on purpose. Hanging up before the INVITE lands makes the BYE fail
   * ("CALL DOES NOT EXIST") and drops the socket — which looks exactly like a dropped call. Without
   * this, the unclean-end path would "helpfully" auto-redial a call the user just cancelled.
   * Cleared when a fresh attempt starts (see _run).
   */
  private _userEnded = false;
  private _lastCall: LastCall | null = null;
  /** True once the current call reached 'connected' — gates auto-redial to BEFORE-answer drops only. */
  private _reachedConnected = false;
  private _redialCount = 0;

  /** orgId_companyId_userId[_jobId] from the token; used to build per-call clientState. */
  private _identity = '';
  /** Cached login JWT + effective expiry so a reconnect reuses it instead of minting a fresh credential. */
  private _token = '';
  private _tokenExpMs = 0;

  // ── Receive mode (inbound calls ring this tab) ─────────────────────────────
  private _receiveMode = false;
  /** The backend web-dialer session this tab registered (one Telnyx credential per tab).
   *  endUrl/endToken = the anonymous tab-close beacon (see _sendEndBeacon). */
  private _session: { id: string; sipUsername: string; endUrl?: string; endToken?: string } | null = null;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _receiveRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private _receiveRetryAttempt = 0;
  private _lifecycleBound = false;
  /** Bumped on every enable/disable; in-flight async receive ops bail when their generation is stale. */
  private _receiveGen = 0;
  /**
   * Bumped ONLY by destroy() (logout / org switch). The receive generation guards receive-mode
   * ops, but _connectClient/_fetchToken also run for OUTBOUND pre-warms and write to mutable
   * token/identity/client fields after several awaits — a token fetch or SDK login that was in
   * flight when the context died could otherwise restore the PREVIOUS account's token and a
   * client logged in as it (review-6 #1). Captured at entry, re-checked after every await.
   */
  private _contextGen = 0;
  /**
   * The backend rejected this tab with 409: the user already has the max number of receiving tabs.
   * While set, the tab stays unregistered (retrying slowly) and _fetchToken falls back to the
   * regular softphone token so OUTBOUND calling keeps working from the un-registered tab.
   */
  private _capBlocked = false;
  /**
   * An admin force-ended THIS tab's session (the WebDialerSessionsEnded broadcast matched our
   * session id). While set, receive mode stays OFF — enableReceiveMode no-ops, so neither the
   * periodic registration re-sync nor the heartbeat self-heal can undo the kick. Cleared only by
   * a page reload or destroy() (logout/org switch = a fresh consent context). Without this latch
   * an admin kick would be re-registered within a minute by the 404 self-heal.
   */
  private _remoteDisconnected = false;
  /**
   * The USER paused receiving on this tab (the indicator menu's "stop receiving here"). Same
   * blocking mechanics as the admin latch — enableReceiveMode no-ops so the periodic re-sync
   * can't silently undo the choice — but cleared by an explicit resume (or reload/destroy).
   */
  private _locallyPaused = false;
  /**
   * Serializes every operation that (re)connects the client or mutates the session — init/pre-warm,
   * receive setup, token refresh, heartbeat self-heal — so a dialer pre-warm can never interleave
   * with a receive reconnect (both tear the client down first; unserialized they flap the
   * registration). NEVER call _serialize from inside a serialized op — that deadlocks the chain;
   * inner helpers (_connectClient, _reconnectForReceive, _createSession) stay unserialized.
   */
  private _opChain: Promise<unknown> = Promise.resolve();

  private _serialize<T>(fn: () => Promise<T>): Promise<T> {
    const run = this._opChain.then(fn, fn);
    this._opChain = run.then(() => undefined, () => undefined);
    return run;
  }

  subscribe(handler: VoiceEventHandler): () => void {
    this._listeners.push(handler);
    handler({ ...this._currentState });
    return () => {
      this._listeners = this._listeners.filter((l) => l !== handler);
    };
  }

  async init(): Promise<void> {
    if (Capacitor.isNativePlatform()) return; // native uses telnyxVoiceManager
    if (this._initialized && this._client) return;
    if (this._initInFlight) return this._initInFlight;
    this._initInFlight = (async () => {
      try {
        await this._serialize(() => this._connectClient());
      } catch (err) {
        // A context change is not a transient failure — retrying would pre-warm a client under
        // the NEW auth context that nobody asked for (review-6 #1).
        if (err instanceof Error && err.message === CONTEXT_CHANGED) throw err;
        // Absorb a single transient login failure; clear the cached token so the retry mints a fresh
        // credential+JWT rather than reusing the one that just failed registration.
        clientLogger.debug('[TelnyxVoiceWeb] login failed; clearing cached token and retrying once:', err);
        this._token = '';
        this._tokenExpMs = 0;
        await this._serialize(() => this._connectClient());
      }
      this._initialized = true;
    })();
    try {
      await this._initInFlight;
    } finally {
      this._initInFlight = null;
    }
  }

  /**
   * ANY call live on this tab — talking, ringing, held or connecting. Every receive-mode guard
   * ("never reconnect mid-call", "defer the session end until the call finishes") reads this, so
   * with call waiting it must stay true while a HELD call exists: ending the session deletes the
   * Telnyx credential those calls ride on.
   * The state fallback covers the window where an outbound dial has emitted 'connecting' but the
   * SDK call object does not exist yet.
   */
  private get _callActive(): boolean {
    if (this._calls.size > 0) return true;
    const s = this._currentState.state;
    // 'incoming' counts as busy (review fix): any future _callActive-only guard must treat a
    // ringing inbound call as active, not idle. Paths that need to distinguish it (endCall, the
    // socket-death handler, invite acceptance) check the roster/state explicitly FIRST.
    return s === 'connecting' || s === 'ringing' || s === 'connected' || s === 'reconnecting' || s === 'incoming';
  }

  /** Live-by-state EXCLUDING a ringing invite — the "was a real call up?" question the socket-death
   *  path asks, for the window before an outbound call's SDK object exists. */
  private get _callActiveByState(): boolean {
    const s = this._currentState.state;
    return s === 'connecting' || s === 'ringing' || s === 'connected' || s === 'reconnecting';
  }

  /** The call the flat event describes (the one the main widget controls). */
  private get _primary(): ManagedCall | null {
    return this._primaryId ? this._calls.get(this._primaryId) ?? null : null;
  }

  /** The un-triaged ringing invite, if any. At most one exists at a time (see _onInboundInvite). */
  private get _incoming(): ManagedCall | null {
    for (const m of this._calls.values()) if (m.role === 'incoming') return m;
    return null;
  }

  /** The call whose audio is live (never more than one — answering/resuming holds the other). */
  private get _activeCall(): ManagedCall | null {
    for (const m of this._calls.values()) if (m.role === 'active') return m;
    return null;
  }

  /** Who becomes primary when the current one goes away: talking first, then held, then ringing. */
  private _pickPrimary(): ManagedCall | null {
    const calls = [...this._calls.values()];
    return calls.find((m) => m.role === 'active')
      ?? calls.find((m) => m.role === 'held')
      ?? calls[0] ?? null;
  }

  async destroy(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    // ── Synchronous capture + reset — nothing may await before this block completes ──────────
    // destroy() runs at logout/org switch and races a re-enable under the NEW context (review-5
    // #1): the old implementation awaited the session DELETE first, and its continuation then
    // cleared the token, killed the refresh timer and disconnected whatever client existed —
    // which by that time could be the NEW login's freshly registered one (its DB session kept
    // heartbeating while the browser could no longer ring). So: capture the old resources and
    // null every field synchronously; the async cleanup below touches ONLY the captured objects.
    this._receiveMode = false;
    this._receiveGen++; // invalidates every in-flight receive op (create/refresh/retry/reconnect)
    this._contextGen++; // invalidates in-flight connect/token work from the dying auth context
    this._stopHeartbeat();
    this._clearReceiveRetry();
    this._receiveRetryAttempt = 0;
    this._receiveConnectFailures = 0;
    this._clearRefreshTimer();
    this._clearConnectWatchdog();
    this._clearRedialTimer();
    this._initialized = false;
    // Clear ALL auth material (review-4 critical): destroy runs at logout/org switch, and a
    // surviving token/identity would let the next signed-in user dial — and be call-recorded —
    // as the previous one.
    this._token = '';
    this._tokenExpMs = 0;
    this._identity = '';
    this._capBlocked = false;
    // The admin-kick and user-pause latches are per auth context: a logout/org switch is a
    // fresh consent context, so the NEXT context may register normally.
    this._remoteDisconnected = false;
    this._locallyPaused = false;
    this._lastCall = null;
    // An outbound dial that is mid-flight (awaiting mic/init) must bail before placing its
    // INVITE under the new context, and nothing of the old context may auto-redial.
    this._abortNextCall = true;
    this._userEnded = true;
    const session = this._session;
    this._session = null;
    // Capture the whole roster (call waiting: there can be a held call and/or a ringing invite
    // alongside the live one) and empty it before the first await, same reason as everything else
    // in this block — the async tail must touch only these captured objects.
    const calls = [...this._calls.values()];
    this._calls.clear();
    this._primaryId = null;
    const client = this._client;
    this._client = null;
    // Reset the state machine NOW (review-5 #2): _callActive derives from _currentState, and a
    // stale 'connected'/'incoming' surviving the teardown made every future receive reconnect
    // refuse forever ("never mid-call") and left the deferred session-end poll spinning on a call
    // that no longer existed — a permanent phantom-busy tab.
    if (this._currentState.state !== 'idle') this._emit({ state: 'idle' });

    // ── Async best-effort cleanup of the CAPTURED (old-context) resources only ───────────────
    // A call live at this moment is deliberately torn down with its context (see the facade doc):
    // hang up over the still-open socket first, then end the session (which deletes the Telnyx
    // credential that carried the call), then drop the socket.
    for (const m of calls) this._releaseAudioEl(m);
    for (const m of calls) {
      if (m.role === 'incoming') { try { void m.call.hangup(); } catch { /* ignore */ } }
    }
    for (const m of calls) {
      if (m.role !== 'incoming') { try { await m.call.hangup(); } catch { /* best effort */ } }
    }
    if (session) {
      // Beacon first (live-testing fix): at LOGOUT the auth tokens may already be gone by the
      // time this runs, so the authenticated DELETE below can 401 — the user then lingered in
      // "Receiving now" for the liveness window. The proof-based beacon needs no auth.
      this._sendEndBeacon(session);
      try {
        await apiService.endWebDialerSession(session.id);
        this._clearSessionRecordIf(session.id);
      } catch { /* best effort — the record stays and the next registration reaps it */ }
    }
    if (client) { try { await client.disconnect(); } catch { /* ignore */ } }
  }

  // ── Receive mode ────────────────────────────────────────────────────────────

  get receiveModeActive(): boolean { return this._receiveMode; }

  /** This tab's backend session id, for matching admin-kick broadcasts. Null when unregistered. */
  get currentReceiveSessionId(): string | null { return this._session?.id ?? null; }

  /**
   * Coarse registration health for the status indicator (polled — deliberately NOT evented, so
   * the connect state machine stays untouched):
   * - 'off'                — receive mode not enabled (not a ring target / mobile / native).
   * - 'registered'         — session live and the SDK client is connected.
   * - 'reconnecting'       — receive mode on but mid-setup/self-heal/retry.
   * - 'blocked'            — the per-user session cap rejected this tab (409); retrying slowly.
   * - 'paused'             — the user stopped receiving on this tab (resumable from the menu).
   * - 'remote-disconnected' — an admin force-ended this tab's session; off until reload.
   */
  get receiveStatus(): 'off' | 'registered' | 'reconnecting' | 'blocked' | 'paused' | 'remote-disconnected' {
    if (this._remoteDisconnected) return 'remote-disconnected';
    if (this._locallyPaused) return 'paused';
    if (!this._receiveMode) return 'off';
    if (this._capBlocked) return 'blocked';
    if (this._session && this._client && this._initialized) return 'registered';
    return 'reconnecting';
  }

  /**
   * An admin force-ended this tab's session (review-6): disable receive mode and LATCH it off —
   * see _remoteDisconnected. The session row is already Ended server-side, so there is nothing to
   * DELETE here; disableReceiveMode's best-effort DELETE is idempotent anyway.
   */
  async markRemoteDisconnected(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    this._remoteDisconnected = true;
    await this.disableReceiveMode();
  }

  /** User choice from the indicator menu: stop receiving on THIS tab until resumed (or reload). */
  async pauseReceive(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    this._locallyPaused = true;
    await this.disableReceiveMode();
  }

  /** Clears the pause latch; the caller re-runs the registration sync to actually re-register. */
  resumeReceive(): void {
    this._locallyPaused = false;
  }

  /**
   * Register this tab to receive inbound calls: create a web-dialer session (per-tab Telnyx
   * credential), connect with its token, and keep the session alive with heartbeats. Failures
   * retry with backoff — a ring target must fight to stay registered.
   */
  async enableReceiveMode(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    // Admin-kicked: stay down until reload / context change (the periodic re-sync and the
    // InboundRoutingChanged handler both funnel through here — the latch must beat them all).
    if (this._remoteDisconnected || this._locallyPaused) return;
    if (this._receiveMode) return;
    this._receiveMode = true;
    this._receiveGen++;
    this._capBlocked = false; // fresh attempt — a slot may have opened since the last 409
    this._receiveConnectFailures = 0;
    this._bindLifecycle();
    this._startHeartbeat();
    await this._ensureReceiveSession();
  }

  /** Unregister: end the session and fall back to lazy, outbound-only behavior. */
  async disableReceiveMode(): Promise<void> {
    if (!this._receiveMode) return;
    this._receiveMode = false;
    // Invalidate every in-flight receive op (session create, reconnect, refresh) — a stale op
    // completing after this point must not resurrect a session or reconnect the client.
    this._receiveGen++;
    this._stopHeartbeat();
    this._clearReceiveRetry();
    // A pending JWT-refresh timer for the (now dead) session token must not fire later and
    // resurrect it / tear the client down (review fix).
    this._clearRefreshTimer();
    this._receiveRetryAttempt = 0;
    // Receiving is off: decline anything still ringing (a call already answered keeps running —
    // its session end is deferred below until it finishes).
    for (const m of [...this._calls.values()]) if (m.role === 'incoming') this.rejectIncoming(m.id);
    const session = this._session;
    this._session = null;
    // The session token is no longer valid auth: clear it so the next outbound call mints a
    // regular softphone token.
    this._token = '';
    this._tokenExpMs = 0;
    const disableGen = this._receiveGen;
    if (session) {
      if (this._callActive) {
        // A live call may be running on this session's credential (review fix): ending the session
        // deletes the credential at Telnyx, which can kill the call. New rings are already blocked
        // (_receiveMode is off ⇒ invites rejected); defer the DELETE until the call ends.
        this._endSessionWhenIdle(session.id);
      } else {
        try {
          await apiService.endWebDialerSession(session.id);
          this._clearSessionRecordIf(session.id);
        } catch { /* best effort — a kept record just gets re-ended (idempotent) by the next page */ }
        // Re-check after the await (review-4): receive mode may have been RE-ENABLED while the
        // DELETE was in flight — tearing down now would kill the replacement session's client.
        if (this._receiveGen !== disableGen || this._receiveMode) return;
        if (!this._callActive) this._markDisconnected();
      }
    } else if (!this._callActive) {
      this._markDisconnected();
    }
  }

  /** Polls until no call is live, then ends the session. Cheap, bounded by the call's length. */
  private _endSessionWhenIdle(sessionId: string): void {
    // Generation at scheduling time (disableReceiveMode bumps it just before calling here). If a
    // destroy or re-enable bumps it again while this timer waits, the OLD session must still die
    // server-side, but the teardown below must not touch the client — it belongs to the newer
    // context by then (review-5 #1-adjacent).
    const gen = this._receiveGen;
    const attempt = () => {
      // Receive mode re-enabled meanwhile: if it reuses a NEW session this old one still must die;
      // only skip if the same id somehow became current again (it can't — ids are server-minted).
      if (this._callActive) { setTimeout(attempt, 5_000); return; }
      void apiService.endWebDialerSession(sessionId)
        .then(() => this._clearSessionRecordIf(sessionId))
        .catch(() => undefined);
      // The client is still logged in with the now-deleted session credential; init() would happily
      // reuse it for the next outbound call and fail (round-3 review fix). Tear down so the next
      // call reconnects with a fresh softphone token — unless receive mode was re-enabled, in which
      // case its own session/connect flow owns the client.
      if (this._receiveGen === gen && !this._receiveMode && !this._callActive) this._markDisconnected();
    };
    setTimeout(attempt, 5_000);
  }

  /**
   * Answer a ringing inbound call. With another call already up this is CALL WAITING: the talking
   * call is put on hold first (locally — synchronously and unfailably — with the server-side hold
   * chasing in the background), then the new call is answered. `callSid` picks which invite when
   * the UI has one in hand; omitted, it answers the only ringing one (the modal's usage).
   */
  async answerIncoming(callSid?: string): Promise<void> {
    const m = callSid ? this._calls.get(callSid) : this._incoming;
    if (!m || m.role !== 'incoming') return;
    // Two rapid Answer clicks (or Answer racing a Hold) must not interleave two hold sequences.
    if (this._callControlInFlight) return;
    this._callControlInFlight = true;
    try {
      if (!(await this._ensureMic())) {
        // _ensureMic surfaced the error; reject so the other Dial legs keep ringing.
        this.rejectIncoming(m.id);
        return;
      }
      // The ring can end during the mic prompt (caller hung up / another tab won) — answering the
      // stale call object would strand the UI on a dead leg (review fix).
      if (this._calls.get(m.id) !== m || m.role !== 'incoming') return;

      // Hold whatever is talking BEFORE answering: the SDK never auto-holds (verified against the
      // bundle), so without this both callers would be live on the same mic.
      const active = this._activeCall;
      if (active && active !== m) this._holdCall(active);

      m.role = 'active';
      m.audioEl = this._createCallAudioEl();
      this._primaryId = m.id;
      this._userEnded = false;
      this._reachedConnected = false;
      this._lastCall = null; // inbound calls have no redial semantics
      try {
        m.call.answer({ remoteElement: m.audioEl });
      } catch (err) {
        clientLogger.debug('[TelnyxVoiceWeb] answer failed:', err);
        this._dropCall(m, true);
        if (this._calls.size > 0) this._emitPrimaryState();
        else this._emit({ state: 'idle' });
        return;
      }
      // 'answering' → 'active' notifications drive the rest; show progress immediately.
      this._emitPrimaryState({ state: 'connecting' });
    } finally {
      this._callControlInFlight = false;
    }
  }

  /** Decline a ringing inbound call. Other Dial legs (tabs / fallback phone) keep ringing, and
   *  any call already up on this tab is untouched. */
  rejectIncoming(callSid?: string): void {
    const m = callSid ? this._calls.get(callSid) : this._incoming;
    if (!m || m.role !== 'incoming') return;
    this._dropCall(m, true);
    if (this._calls.size > 0) this._emitPrimaryState();
    else if (this._currentState.state === 'incoming') this._emit({ state: 'idle' });
  }

  /**
   * Hold or resume a specific call. Resuming holds whatever else is talking first, so exactly one
   * call is ever live — and makes the resumed call primary, which is how the UI "switches" calls.
   */
  async setHold(callSid: string, held: boolean): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    const m = this._calls.get(callSid);
    if (!m || m.role === 'incoming') return;
    if (this._callControlInFlight) return;
    this._callControlInFlight = true;
    try {
      if (held) {
        if (m.role === 'held') return;
        this._holdCall(m);
        // Holding the only/primary call leaves it primary — there is nothing else to show.
      } else {
        const active = this._activeCall;
        if (active && active !== m) this._holdCall(active);
        m.intendedHeld = false;
        m.role = 'active';
        this._localResume(m);
        this._primaryId = m.id;
        void this._serverHold(m, false);
      }
      this._emitPrimaryState();
    } finally {
      this._callControlInFlight = false;
    }
  }

  /**
   * Put a call on hold: local layer FIRST (synchronous, cannot fail), server-side hold in the
   * background. Order matters — the server hold is a round trip that can fail or be slow, and
   * until it lands the local layer is what guarantees the held caller hears nothing and is not
   * heard. See _localHold.
   */
  private _holdCall(m: ManagedCall): void {
    m.intendedHeld = true;
    m.role = 'held';
    this._localHold(m);
    void this._serverHold(m, true);
  }

  /** Local hold: stop sending this call our mic, stop playing its audio. Both are local operations
   *  on objects we own, so unlike the signaling hold they cannot fail or lag. */
  private _localHold(m: ManagedCall): void {
    m.localHold = true;
    try { m.call.muteAudio(); } catch { /* ignore */ }
    if (m.audioEl) m.audioEl.muted = true;
  }

  /** Undo _localHold, restoring the user's own mute choice for that call (not blindly unmuting). */
  private _localResume(m: ManagedCall): void {
    m.localHold = false;
    if (m.audioEl) m.audioEl.muted = false;
    try {
      if (m.isMuted) m.call.muteAudio();
      else m.call.unmuteAudio();
    } catch { /* ignore */ }
  }

  /**
   * The signaling half of hold (telnyx_rtc.modify). The SDK's hold()/unhold() never REJECT — they
   * resolve false on failure — so this checks the resolved value and retries once before giving
   * up. Failure is not fatal: the local layer already contains the call; the warning only tells
   * the user the far end may not be formally held.
   */
  private async _serverHold(m: ManagedCall, held: boolean): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt++) {
      let ok = false;
      try { ok = (await (held ? m.call.hold() : m.call.unhold())) !== false; } catch { ok = false; }
      // The call ended, or the user changed their mind meanwhile — a newer op owns the state now.
      if (this._calls.get(m.id) !== m || m.intendedHeld !== held) return;
      if (ok) return;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 500));
    }
    clientLogger.debug(`[TelnyxVoiceWeb] server ${held ? 'hold' : 'unhold'} failed for call ${m.id}`);
    if (held) {
      this._emit({ ...this._currentState, error: 'Could not fully hold the other call.' });
    }
  }

  /** Create the backend session + register. Serialized; safe to call from any trigger. */
  private async _ensureReceiveSession(): Promise<void> {
    const gen = this._receiveGen;
    await this._serialize(async () => {
      if (!this._receiveMode || gen !== this._receiveGen) return;
      try {
        if (!this._session) await this._createSession(gen);
        if (!this._receiveMode || gen !== this._receiveGen) return;
        await this._reconnectForReceive();
        this._receiveRetryAttempt = 0;
      } catch (err: any) {
        // 403 on session CREATE = a definitive refusal (subscription voice gate / revoked
        // permission), not an outage — and with no session there are no heartbeats, so nothing
        // else would ever stop the loop: the old backoff retried the gate forever (review-8 #6b).
        // Mirror the heartbeat's revocation handling and stand down; the periodic registration
        // re-sync re-enables if policy changes (its /me check fails the same way until then).
        if (err?.response?.status === 403 && gen === this._receiveGen) {
          clientLogger.debug('[TelnyxVoiceWeb] session create rejected (403) — disabling receive mode');
          void this.disableReceiveMode();
          return;
        }
        clientLogger.debug('[TelnyxVoiceWeb] receive session setup failed; will retry:', err);
        if (gen === this._receiveGen) {
          this._scheduleReceiveRetry(
            this._capBlocked ? WebTelnyxVoiceManager.CAP_BLOCK_RETRY_MS : undefined);
        }
      }
    });
  }

  /**
   * The session-create step (API call + local state + credential-propagation wait). NOT serialized —
   * always runs inside an already-serialized op (or _fetchToken within _connectClient).
   */
  private async _createSession(gen: number): Promise<void> {
    // A page REFRESH leaks the predecessor page's session when its pagehide beacon is lost
    // (sendBeacon only promises to QUEUE the request; whether the public ingress ever receives
    // it is outside our control) — each leak burns a ring slot and a cap slot for the whole
    // liveness window. The successor page ends it deterministically before registering itself,
    // which also frees the cap slot the create below is about to need. Orphans parked by
    // CLOSED tabs (no successor, no surviving tab at the time) are reaped here too.
    await this._reapPredecessorSession();
    this._reapOrphanSessions();
    if (gen !== this._receiveGen) throw new Error('receive mode changed during session create');
    let s;
    try {
      s = await apiService.createWebDialerSession();
    } catch (err: any) {
      // 409 = per-user session cap. Stay unregistered and retry slowly — the backend deliberately
      // never evicts (an evict + self-heal loop across 6 tabs would mint credentials forever).
      if (err?.response?.status === 409) this._capBlocked = true;
      throw err;
    }
    this._capBlocked = false;
    if (gen !== this._receiveGen) {
      // Disabled/re-enabled while creating: this session will never be used — release it.
      try { await apiService.endWebDialerSession(s.sessionId); } catch { /* best effort */ }
      throw new Error('receive mode changed during session create');
    }
    this._session = { id: s.sessionId, sipUsername: s.sipUsername, endUrl: s.endUrl, endToken: s.endToken };
    this._storeSessionRecord(this._session);
    this._identity = s.identity;
    this._setSessionToken(s.token);
    await new Promise((r) => setTimeout(r, WebTelnyxVoiceManager.CREDENTIAL_PROPAGATION_MS));
  }

  private _setSessionToken(token: string): void {
    this._token = token;
    const now = Date.now();
    const jwtExpMs = this._decodeJwtExpMs(token);
    this._tokenExpMs = jwtExpMs ? jwtExpMs - 60_000 : now + 23 * 3_600_000;
    this._scheduleRefresh(this._tokenExpMs - now);
  }

  /**
   * A valid session JWT: refresh from the existing session, or — when the backend evicted/expired
   * it (404) — transparently create a replacement session (the self-heal contract). Returns whether
   * the credential rotated (new SIP username ⇒ propagation wait + reconnect required).
   */
  private async _obtainSessionToken(gen: number): Promise<{ rotated: boolean }> {
    const session = this._session;
    if (!session) {
      await this._createSession(gen);
      return { rotated: true };
    }
    try {
      const r = await apiService.refreshWebDialerToken(session.id);
      // Receive mode may have been disabled/re-enabled while the request was in flight — a stale
      // refresh must not restore its token over the new generation's state (review fix).
      if (gen !== this._receiveGen) throw new Error('receive mode changed during token refresh');
      session.sipUsername = r.sipUsername;
      this._setSessionToken(r.token);
      if (r.rotated) await new Promise((res) => setTimeout(res, WebTelnyxVoiceManager.CREDENTIAL_PROPAGATION_MS));
      return { rotated: r.rotated };
    } catch (err: any) {
      if (err?.response?.status !== 404) throw err;
      if (gen !== this._receiveGen) throw new Error('receive mode changed during token refresh');
      this._session = null;
      await this._createSession(gen);
      return { rotated: true };
    }
  }

  /** Refresh-timer path: new token, then reconnect so the registration never lapses. Serialized. */
  private async _refreshReceiveToken(): Promise<void> {
    const gen = this._receiveGen;
    await this._serialize(async () => {
      if (!this._receiveMode || gen !== this._receiveGen) return;
      // Never tear down a live call to re-register; check again shortly.
      if (this._callActive) { this._scheduleRefresh(60_000); return; }
      try {
        // Propagation wait happens inside only when the credential actually rotated (review fix:
        // a routine JWT refresh must not add a ~5s deaf window).
        await this._obtainSessionToken(gen);
        if (!this._receiveMode || gen !== this._receiveGen) return;
        await this._reconnectForReceive();
      } catch (err) {
        clientLogger.debug('[TelnyxVoiceWeb] receive token refresh failed; will retry:', err);
        if (gen === this._receiveGen) this._scheduleReceiveRetry();
      }
    });
  }

  /** Consecutive failed receive (re)connects; gates heartbeats — see SUPPRESS_BEATS_AFTER_FAILURES. */
  private _receiveConnectFailures = 0;

  /** (Re)connect for receiving — but never mid-call; the retry loop picks it up after. */
  private async _reconnectForReceive(): Promise<void> {
    if (!this._receiveMode) return;
    if (this._callActive) { this._scheduleReceiveRetry(); return; }
    try {
      await this._connectClient();
    } catch (err) {
      this._receiveConnectFailures++;
      throw err;
    }
    this._initialized = true;
    if (this._receiveConnectFailures > 0) {
      // Recovered: resume liveness NOW — the session may have aged close to (or past) the 5-min
      // window while beats were suppressed; a prompt beat revives it or 404s into the self-heal.
      this._receiveConnectFailures = 0;
      void this._beat();
    }
  }

  private _startHeartbeat(): void {
    this._stopHeartbeat();
    this._heartbeatTimer = setInterval(() => { void this._beat(); }, WebTelnyxVoiceManager.HEARTBEAT_MS);
  }

  private _stopHeartbeat(): void {
    if (this._heartbeatTimer !== null) { clearInterval(this._heartbeatTimer); this._heartbeatTimer = null; }
  }

  private async _beat(): Promise<void> {
    const session = this._session;
    const gen = this._receiveGen;
    if (!this._receiveMode || !session) return;
    // Sustained connect failure = this tab can't actually RECEIVE anything — stop refreshing the
    // DB session's liveness so ring plans drop it (review-6 #5). The retry loop keeps trying to
    // reconnect; success resets the counter and beats immediately (see _reconnectForReceive).
    if (this._receiveConnectFailures >= WebTelnyxVoiceManager.SUPPRESS_BEATS_AFTER_FAILURES) return;
    try {
      await apiService.webDialerHeartbeat(session.id);
    } catch (err: any) {
      // 403 = the voip.call permission was revoked — deregister NOW, not when the liveness window
      // expires (round-3 review fix). 401 is left to the axios interceptor's token-refresh flow.
      if (err?.response?.status === 403 && gen === this._receiveGen) {
        clientLogger.debug('[TelnyxVoiceWeb] heartbeat rejected (403) — disabling receive mode');
        void this.disableReceiveMode();
        return;
      }
      // 404 = expired/ended by the backend — self-heal with a fresh session.
      // _ensureReceiveSession is serialized, so this can't interleave with a refresh/reconnect.
      if (err?.response?.status === 404 && gen === this._receiveGen && this._session === session) {
        // Kill the dead session's pending JWT-refresh timer (review fix): its fall-through path
        // would _markDisconnected() and tear the client down while the replacement is connecting.
        this._clearRefreshTimer();
        this._session = null;
        this._token = '';
        this._tokenExpMs = 0;
        void this._ensureReceiveSession();
        // The dead session may belong to another org context (multi-org tab switch): end it
        // explicitly so its row doesn't linger in that org's ring plans until cleanup.
        try { void apiService.endWebDialerSession(session.id).catch(() => undefined); } catch { /* best effort */ }
      }
      // Anything else is transient; the next beat retries.
    }
  }

  private _bindLifecycle(): void {
    if (this._lifecycleBound) return;
    this._lifecycleBound = true;
    window.addEventListener('pagehide', (e: PageTransitionEvent) => {
      // Real unload only. A bfcache freeze (persisted=true) keeps the session — pageshow
      // revalidates it; ending it here would deregister a tab that comes right back.
      if (!e.persisted && this._receiveMode && this._session) {
        // Beacon FIRST (live-testing fix): the keepalive DELETE carries auth headers → CORS
        // preflight → Chrome drops it at unload, so closed tabs stayed "receiving" for the whole
        // liveness window. sendBeacon is preflight-free and survives unload. The keepalive fetch
        // stays as a fallback for sessions minted by a pre-beacon backend.
        if (!this._sendEndBeacon(this._session)) {
          apiService.endWebDialerSessionKeepalive(this._session.id);
        }
        // Belt and braces (live-testing fix #4): the beacon's DELIVERY is unconfirmed, so also
        // hand the session to the shared orphan list — a surviving tab (or the next page in
        // this tab) ends it server-side within moments instead of after the liveness window.
        this._recordOrphanSession(this._session);
      }
    });
    // Another tab of this origin just closed and recorded its session — reap it now, so the
    // "Receiving now" list drops the closed tab within seconds, and its ring/cap slots free up.
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key !== WebTelnyxVoiceManager.ORPHANS_KEY || !e.newValue) return;
      this._reapOrphanSessions();
    });
    window.addEventListener('pageshow', (e: PageTransitionEvent) => {
      if (!e.persisted || !this._receiveMode) return;
      void this._beat();
      // The SDK socket may have died silently during the bfcache freeze — a live DB session
      // pointing at a dead socket rings nothing (round-3 review fix). Force a fresh registration;
      // serialized, so it queues cleanly behind any 404 self-heal the heartbeat just started.
      this._forceReceiveReconnect();
    });
    // Chromium also FREEZES plain background tabs (Page Lifecycle API) without bfcache — same
    // silent-socket-death failure mode, but no pageshow ever fires (review-5 #d). 'resume' is
    // Chromium-only; browsers that don't implement it don't freeze plain background tabs either.
    document.addEventListener('resume', () => {
      if (!this._receiveMode) return;
      void this._beat();
      this._forceReceiveReconnect();
    });
    // Cheap revalidation whenever the tab returns to the foreground: an expired session self-heals
    // on the heartbeat 404 immediately instead of waiting out the current 60s beat interval.
    // Deliberately NOT a forced reconnect — alt-tabbing must not flap a healthy registration.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this._receiveMode && this._session) void this._beat();
    });
  }

  /** Record the live session so the page that replaces this one in the same tab (refresh /
   *  navigation) can end it — see _reapPredecessorSession. Storage failures (private-mode
   *  quotas) are absorbed: the beacon and the liveness sweep remain the fallback cleanup. */
  private _storeSessionRecord(session: { id: string; endUrl?: string; endToken?: string }): void {
    try {
      sessionStorage.setItem(
        WebTelnyxVoiceManager.SESSION_RECORD_KEY,
        JSON.stringify({ id: session.id, endUrl: session.endUrl, endToken: session.endToken }));
    } catch { /* best effort */ }
  }

  /** Drop the record once its session is KNOWN to be ended server-side. Guarded by id so a
   *  late cleanup of an old session can never wipe a newer session's record. */
  private _clearSessionRecordIf(sessionId: string): void {
    try {
      const raw = sessionStorage.getItem(WebTelnyxVoiceManager.SESSION_RECORD_KEY);
      if (raw && JSON.parse(raw)?.id === sessionId) {
        sessionStorage.removeItem(WebTelnyxVoiceManager.SESSION_RECORD_KEY);
      }
    } catch { /* best effort */ }
  }

  /**
   * End the PREVIOUS page's session (the refresh-leak fix). pagehide's sendBeacon is queued, not
   * confirmed — when its delivery fails, the old row stays Active for the whole liveness window,
   * so every refresh visibly adds a "receiving tab" and can crowd real tabs out of ring plans.
   * The successor page owns the cleanup instead: the beacon (anonymous, also covers a
   * predecessor from another org context) plus the authenticated DELETE, both idempotent
   * server-side. The DELETE is awaited so the create that follows sees the freed cap slot.
   *
   * Known trade-off: "Duplicate tab" COPIES sessionStorage, so the duplicate reaps the
   * original tab's live session once — the original's heartbeat-404 self-heal re-registers it
   * within a beat (≤60s deaf window, rare user action, accepted).
   */
  private async _reapPredecessorSession(): Promise<void> {
    let rec: { id?: string; endUrl?: string; endToken?: string } | null = null;
    try {
      const raw = sessionStorage.getItem(WebTelnyxVoiceManager.SESSION_RECORD_KEY);
      rec = raw ? JSON.parse(raw) : null;
    } catch { rec = null; }
    if (!rec?.id || rec.id === this._session?.id) return;
    this._clearSessionRecordIf(rec.id);
    // A refresh parks the session in BOTH records (pagehide can't tell refresh from close);
    // this path owns it now, so drop the orphan entry rather than ending it twice.
    this._removeOrphanRecord(rec.id);
    this._sendEndBeacon({ id: rec.id, endUrl: rec.endUrl, endToken: rec.endToken });
    try { await apiService.endWebDialerSession(rec.id); } catch { /* already gone / other org */ }
  }

  /** pagehide: park this session in the shared cross-tab orphan list (see ORPHANS_KEY). */
  private _recordOrphanSession(session: { id: string; endUrl?: string; endToken?: string }): void {
    try {
      const raw = localStorage.getItem(WebTelnyxVoiceManager.ORPHANS_KEY);
      const map = raw ? JSON.parse(raw) : {};
      map[session.id] = { endUrl: session.endUrl, endToken: session.endToken };
      localStorage.setItem(WebTelnyxVoiceManager.ORPHANS_KEY, JSON.stringify(map));
    } catch { /* best effort — the beacon and the liveness sweep remain */ }
  }

  /** Remove one entry from the orphan list (already ended through another path). */
  private _removeOrphanRecord(sessionId: string): void {
    try {
      const raw = localStorage.getItem(WebTelnyxVoiceManager.ORPHANS_KEY);
      if (!raw) return;
      const map = JSON.parse(raw);
      if (!(sessionId in map)) return;
      delete map[sessionId];
      if (Object.keys(map).length === 0) localStorage.removeItem(WebTelnyxVoiceManager.ORPHANS_KEY);
      else localStorage.setItem(WebTelnyxVoiceManager.ORPHANS_KEY, JSON.stringify(map));
    } catch { /* best effort */ }
  }

  /**
   * End every session parked in the orphan list by tabs that closed (fired from the 'storage'
   * event in surviving tabs, and once at registration for the "closed the last tab, opened a
   * new one later" case). Entries are claimed (removed) before ending so concurrent survivors
   * mostly don't duplicate the work — duplicates are idempotent server-side anyway. The beacon
   * covers an orphan from another user/org signed into this browser; the authenticated DELETE
   * covers the common same-user case even when the public ingress drops browser POSTs.
   */
  private _reapOrphanSessions(): void {
    let map: Record<string, { endUrl?: string; endToken?: string }> = {};
    try {
      const raw = localStorage.getItem(WebTelnyxVoiceManager.ORPHANS_KEY);
      map = raw ? JSON.parse(raw) : {};
    } catch { return; }
    const ids = Object.keys(map).filter((id) => id !== this._session?.id);
    if (ids.length === 0) return;
    for (const id of ids) this._removeOrphanRecord(id);
    for (const id of ids) {
      this._sendEndBeacon({ id, endUrl: map[id]?.endUrl, endToken: map[id]?.endToken });
      void apiService.endWebDialerSession(id).catch(() => undefined);
    }
  }

  /** Fire the anonymous end-session beacon. True = handed to the browser (delivery is
   *  fire-and-forget by design); false = this session predates the beacon contract. */
  private _sendEndBeacon(session: { id: string; endUrl?: string; endToken?: string }): boolean {
    if (!session.endUrl || !session.endToken || typeof navigator.sendBeacon !== 'function') return false;
    try {
      return navigator.sendBeacon(session.endUrl, JSON.stringify({ sessionId: session.id, proof: session.endToken }));
    } catch {
      return false;
    }
  }

  /** Serialized socket-health recovery after a tab freeze (bfcache or Page Lifecycle 'resume'):
   *  reconnect unless a call is live; failures fall into the standard receive retry loop. */
  private _forceReceiveReconnect(): void {
    void this._serialize(async () => {
      if (this._receiveMode && this._session && !this._callActive) {
        try { await this._reconnectForReceive(); } catch (err) {
          clientLogger.debug('[TelnyxVoiceWeb] lifecycle reconnect failed; will retry:', err);
          this._scheduleReceiveRetry();
        }
      }
    });
  }

  private _scheduleReceiveRetry(overrideDelayMs?: number): void {
    if (!this._receiveMode) return;
    this._clearReceiveRetry();
    const delay = overrideDelayMs ?? Math.min(
      WebTelnyxVoiceManager.RECEIVE_RETRY_BASE_MS * 2 ** this._receiveRetryAttempt,
      WebTelnyxVoiceManager.RECEIVE_RETRY_MAX_MS,
    );
    this._receiveRetryAttempt++;
    const gen = this._receiveGen;
    this._receiveRetryTimer = setTimeout(() => {
      if (!this._receiveMode || gen !== this._receiveGen) return;
      if (!this._session) { void this._ensureReceiveSession(); return; }
      void this._serialize(async () => {
        if (!this._receiveMode || gen !== this._receiveGen) return;
        try {
          await this._reconnectForReceive();
          this._receiveRetryAttempt = 0;
        } catch (err) {
          clientLogger.debug('[TelnyxVoiceWeb] receive reconnect failed; will retry:', err);
          if (gen === this._receiveGen) this._scheduleReceiveRetry();
        }
      });
    }, delay);
  }

  private _clearReceiveRetry(): void {
    if (this._receiveRetryTimer !== null) { clearTimeout(this._receiveRetryTimer); this._receiveRetryTimer = null; }
  }

  async makeCall(toE164: string, callerIdE164: string, displayName?: string, jobId?: string): Promise<void> {
    this._lastCall = { toE164, callerIdE164, displayName, jobId };
    this._redialCount = 0;
    await this._run();
  }

  /** Masked calls are mobile-only. */
  async makeMaskedCall(): Promise<void> {
    clientLogger.debug('[TelnyxVoiceWeb] makeMaskedCall is not supported on web (mobile-only feature).');
    this._emit({ state: 'ended', error: 'Masked calling is only available in the mobile app.' });
    this._scheduleIdle();
  }

  /** Guards concurrent _run entries: two rapid dial actions both pass the provider's idle check
   *  while the first awaits the mic prompt, and would otherwise place TWO PSTN calls (review-4). */
  private _dialInFlight = false;

  /**
   * Serializes answer / hold / resume. Same shape as _dialInFlight and deliberately NOT _opChain:
   * that mutex serializes CONNECTION work and these run inside user gestures — nesting one in the
   * other deadlocks the chain. Without it, a double-clicked Answer (or Answer racing Hold) can
   * interleave two hold sequences and leave both calls thinking they own the mic.
   */
  private _callControlInFlight = false;

  /** The audio element created for a dial that is still inside newCall(); the SDK's construction-
   *  time notification adopts the call before newCall() returns, and picks the element up here. */
  private _pendingDialAudioEl: HTMLAudioElement | null = null;

  private async _run(): Promise<void> {
    const c = this._lastCall;
    if (!c) return;
    if (Capacitor.isNativePlatform()) return;
    if (this._dialInFlight) {
      clientLogger.debug('[TelnyxVoiceWeb] outbound call refused: another dial is already starting');
      return;
    }
    this._dialInFlight = true;
    try {
      await this._runInner(c);
    } finally {
      this._dialInFlight = false;
    }
  }

  private async _runInner(c: LastCall): Promise<void> {
    // Call waiting is INBOUND-only: a second line can arrive, it can't be dialed. Outbound stays
    // refused while anything is live — ringing, talking or held (the provider guards this too).
    if (this._calls.size > 0) {
      clientLogger.debug('[TelnyxVoiceWeb] outbound call refused: another call is already live');
      return;
    }
    if (!(await this._ensureMic())) return;
    // Re-check after the mic prompt (review fix): an inbound invite can arrive while the user
    // stares at the permission dialog — the incoming ring wins; the user can dial after deciding.
    if (this._calls.size > 0) {
      clientLogger.debug('[TelnyxVoiceWeb] outbound call abandoned: an incoming call arrived');
      return;
    }

    // A fresh attempt supersedes any stale abort/user-hangup flag from a previous one.
    this._abortNextCall = false;
    this._userEnded = false;
    this._reachedConnected = false;
    this._clearRedialTimer();
    // Show the call UI immediately, then log in + invite below.
    this._emit({
      state: 'connecting',
      toE164: c.toE164, callerIdE164: c.callerIdE164, displayName: c.displayName,
      isMuted: false, availableAudioDevices: [],
    });

    let audioEl: HTMLAudioElement | null = null;
    try {
      await this.init();
      if (this._abortNextCall) { this._abortNextCall = false; return; }
      // Defensive (review): state 'connecting' auto-rejects invites during init, so this should be
      // unreachable — but an incoming call must never be silently clobbered by an outbound dial.
      if (this._calls.size > 0) { clientLogger.debug('[TelnyxVoiceWeb] outbound abandoned: incoming call arrived'); return; }
      if (!this._client) throw new Error('Softphone not connected.');
      void this._refreshAudioDevices();
      audioEl = this._createCallAudioEl();
      // newCall() dispatches a state-'new' notification DURING construction, before it returns —
      // the adoption path below picks the element up from here so the call is never adopted sinkless.
      this._pendingDialAudioEl = audioEl;
      const call = this._client.newCall({
        destinationNumber: c.toE164,
        callerNumber: c.callerIdE164,
        callerName: c.displayName,
        remoteElement: audioEl,
        customHeaders: [{ name: 'X-Client-State', value: this._buildClientState(c.jobId, c.callerIdE164) }],
      });
      this._adoptOutbound(call, audioEl);
      audioEl = null; // the roster owns it now
    } catch (err) {
      if (audioEl) { try { audioEl.remove(); } catch { /* ignore */ } }
      this._failCallStart(err);
      throw err;
    } finally {
      this._pendingDialAudioEl = null;
    }
  }

  /** Register an outbound call in the roster. Idempotent: the SDK's construction-time notification
   *  usually adopts it first (see the _dialInFlight branch in _onNotification). */
  private _adoptOutbound(call: TelnyxCall, audioEl?: HTMLAudioElement | null): ManagedCall {
    const existing = this._calls.get(call.id);
    if (existing) {
      if (!existing.audioEl) existing.audioEl = audioEl ?? this._pendingDialAudioEl;
      return existing;
    }
    const m: ManagedCall = {
      id: call.id,
      call,
      direction: 'outbound',
      role: 'active',
      audioEl: audioEl ?? this._pendingDialAudioEl,
      intendedHeld: false,
      localHold: false,
      isMuted: false,
      toE164: this._lastCall?.toE164,
      displayName: this._lastCall?.displayName,
    };
    this._calls.set(m.id, m);
    this._primaryId = m.id;
    return m;
  }

  /**
   * Redial the last call. `manual` (the one-tap button on the ended screen) resets the auto-redial
   * budget; an internal auto-redial keeps the current budget.
   */
  async redial(manual = true): Promise<void> {
    if (!this._lastCall) return;
    if (manual) this._redialCount = 0;
    try {
      await this._run();
    } catch (err) {
      clientLogger.debug('[TelnyxVoiceWeb] redial failed:', err);
    }
  }

  /** End one call. `callSid` targets a specific one (the call list's per-row End); omitted, it
   *  ends the primary — which is what the widget's End button and the facade pass. */
  async endCall(callSid?: string): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    const target = callSid ? this._calls.get(callSid) : (this._primary ?? this._incoming);
    // "End" on a call that is still ringing = decline it.
    if (target?.role === 'incoming') { this.rejectIncoming(target.id); return; }
    this._clearRedialTimer();
    // Record the intent BEFORE any await: the hangup below can fail and tear down the socket, and the
    // resulting error must not be mistaken for a dropped call and auto-redialed.
    this._userEnded = true;
    // Dismissing the post-call (dropped/redial) screen: settle straight to idle.
    if (!target && this._currentState.state === 'ended') { this._emit({ state: 'idle' }); return; }
    // Hang-up during 'connecting': the INVITE may not be placed yet — flag the run path to bail before
    // dialing, so a cancelled call never rings the destination.
    if (this._currentState.state === 'connecting') this._abortNextCall = true;
    if (target) this._dropCall(target, false);
    // A surviving call (the other line, still held) keeps the widget up — no "Call ended" screen,
    // and deliberately no auto-resume: the survivor waits on its Resume button rather than putting
    // a live mic in front of a caller the user wasn't talking to.
    if (this._calls.size > 0) {
      // The user ended ONE line, not "the call": leaving the flag set would make a later unclean
      // drop of the survivor silent — _handleUncleanEnd would take it for a deliberate hangup and
      // never emit 'ended', stranding the widget on a call that is already gone.
      this._userEnded = false;
      this._emitPrimaryState();
    } else if (this._callActiveByState || this._currentState.state === 'incoming') {
      // End the UI now. We can't wait for the SDK's hangup notification: if the INVITE hasn't landed
      // yet the BYE fails ("CALL DOES NOT EXIST") and no notification ever arrives, which would
      // otherwise strand the widget on "Ringing…" until the connect watchdog fires.
      this._emit({ ...this._currentState, state: 'ended', isMuted: false, error: undefined, canRedial: undefined });
      this._scheduleIdle();
    }
    try { await target?.call.hangup(); } catch { /* best effort — see above */ }
  }

  /** Mute one call's microphone. Per-call by SDK design (each call owns its own capture), so this
   *  targets the primary unless told otherwise — a held call's mic is already stopped. */
  async muteCall(muted: boolean, callSid?: string): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    const m = callSid ? this._calls.get(callSid) : this._primary;
    if (m) {
      m.isMuted = muted;
      // While held, the local hold layer owns the mic track; the preference applies on resume.
      if (!m.localHold) {
        try {
          if (muted) m.call.muteAudio();
          else m.call.unmuteAudio();
        } catch { /* ignore */ }
      }
    }
    this._emit({ ...this._currentState, isMuted: this._primary?.isMuted ?? muted });
  }

  async getAudioDevices(): Promise<{ devices: AudioDevice[]; selected: AudioDeviceType }> {
    if (Capacitor.isNativePlatform()) return { devices: [], selected: 'speaker' };
    const devices = await this._enumerateOutputs();
    const selected = devices.find((d) => d.id === this._selectedSpeakerId)?.type ?? devices[0]?.type ?? 'speaker';
    return { devices, selected };
  }

  /** `id` is the browser deviceId (from AudioDevice.id); `type` is only the icon hint.
   *  Applies to EVERY live call — each has its own audio element, and the speaker choice is the
   *  user's, not the call's. */
  async selectAudioDevice(_type: AudioDeviceType, id?: string): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    // setSinkId is Chromium/Firefox-recent; unsupported on Safari (picker is hidden there).
    if (!id || !this._canSetSinkId()) return;
    try {
      for (const m of this._calls.values()) {
        if (m.audioEl) await (m.audioEl as any).setSinkId(id);
        // Keeps the SDK's own per-call speaker bookkeeping in step (it re-applies on 'active').
        try { (m.call as any).setAudioOutDevice?.(id); } catch { /* optional SDK path */ }
      }
      this._selectedSpeakerId = id;
      await this._refreshAudioDevices();
    } catch (err) {
      clientLogger.debug('[TelnyxVoiceWeb] setSinkId failed:', err);
    }
  }

  async sendDigits(digits: string): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    // Digits go to the call the user is actually on; a held call isn't listening.
    this._primary?.call.dtmf(digits);
  }

  get currentState(): VoiceCallEvent { return { ...this._currentState }; }

  // ── Private ─────────────────────────────────────────────────────────────────

  private _buildClientState(jobId: string | undefined, callerId: string): string {
    // identity = orgId_companyId_userId[_jobId]
    const parts = this._identity.split('_');
    // The login token identity freezes the company at login time. A user can switch the active company
    // after logging in, so attribute the call to the CURRENTLY selected company.
    const currentCompanyId = apiService.getCompanyId();
    const state = {
      orgId: parts[0] ?? '',
      companyId: currentCompanyId ?? parts[1] ?? '',
      userId: parts[2] ?? '',
      jobId: jobId ?? (parts.length === 4 ? parts[3] : undefined),
      callerId,
    };
    // base64url (no +, /, =) so it survives SIP-header transport and form-urlencoded webhook decoding.
    return btoa(JSON.stringify(state)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private async _ensureMic(): Promise<boolean> {
    // WebRTC mic capture requires a secure context (HTTPS, or localhost/127.0.0.1). On a plain-HTTP
    // LAN origin `navigator.mediaDevices` is undefined, so calling getUserMedia would throw a bare
    // TypeError — surface a clear, actionable error instead.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      this._failWith(
        'The softphone needs a secure connection. Open the app over HTTPS or via localhost to place calls.',
      );
      return false;
    }
    try {
      // Prompts the browser on first use; also unlocks device labels for the output picker. Stop the
      // tracks immediately — the SDK opens its own stream for the call.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (err: any) {
      const denied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
      this._failWith(
        denied
          ? 'Microphone access blocked. Allow microphone access for this site in your browser settings.'
          : 'Could not access the microphone.',
      );
      return false;
    }
  }

  /** Surface a pre-call failure VISIBLY: state 'ended' (not 'idle') so the widget renders the error,
   *  then settle to idle. An 'idle' emit would hide the widget and swallow the message. */
  private _failWith(error: string): void {
    this._emit({ state: 'ended', error });
    this._scheduleIdle();
  }

  private _emit(event: VoiceCallEvent): void {
    // The roster rides along on every emit, so the multi-call UI never needs its own channel and
    // single-call consumers keep reading the flat fields (which describe the primary call).
    this._currentState = { ...event, calls: this._buildCallSummaries() };
    if (event.state === 'connected') this._reachedConnected = true;
    if (event.state === 'connecting') this._startConnectWatchdog();
    else if (event.state === 'connected' || event.state === 'ended' || event.state === 'idle') {
      this._clearConnectWatchdog();
    }
    const snapshot = this._currentState;
    this._listeners.forEach((l) => l({ ...snapshot }));
  }

  /** Re-emit with the flat fields describing the current primary call. The single place that
   *  translates roster state into the legacy event shape. */
  private _emitPrimaryState(extra?: Partial<VoiceCallEvent>): void {
    const m = this._primary;
    if (!m) {
      if (extra) this._emit({ ...this._currentState, ...extra });
      return;
    }
    this._emit({
      ...this._currentState,
      state: this._legacyStateOf(m),
      callSid: m.id,
      toE164: m.toE164 ?? this._currentState.toE164,
      displayName: m.displayName,
      incomingContext: m.incomingContext,
      isMuted: m.isMuted,
      error: undefined,
      canRedial: undefined,
      ...extra,
    });
  }

  /** How one call maps onto the single-call CallState the widget understands. A HELD call still
   *  reads as 'connected' — it IS an established call, and the "On hold" wording comes from the
   *  roster entry, so no consumer needs a new state value. */
  private _legacyStateOf(m: ManagedCall): VoiceCallEvent['state'] {
    if (m.role === 'incoming') return 'incoming';
    if (m.role === 'held') return 'connected';
    switch (m.call.state as string) {
      case 'active': case 'held': return 'connected';
      case 'ringing': case 'early': return 'ringing';
      case 'recovering': return 'reconnecting';
      default: return 'connecting';
    }
  }

  private _buildCallSummaries(): CallSummary[] | undefined {
    if (this._calls.size === 0) return undefined;
    const summaries: CallSummary[] = [];
    for (const m of this._calls.values()) {
      const legacy = this._legacyStateOf(m);
      summaries.push({
        callSid: m.id,
        direction: m.direction,
        status: m.role === 'held' ? 'held' : (legacy === 'idle' || legacy === 'ended' ? 'connecting' : legacy),
        isPrimary: m.id === this._primaryId,
        isMuted: m.isMuted,
        remoteNumber: m.toE164,
        displayName: m.displayName,
        connectedAt: m.connectedAt,
        incomingContext: m.incomingContext,
      });
    }
    // Primary first — the list UI renders in this order.
    summaries.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    return summaries;
  }

  /**
   * Remove a call from the roster and release its audio element, repointing the primary if it was
   * the one that left. Deliberately does NOT emit: the caller decides between "another call is
   * still up" (re-emit the survivor) and "that was the last one" (the ended/idle screen).
   */
  private _dropCall(m: ManagedCall, hangup: boolean): void {
    this._calls.delete(m.id);
    this._releaseAudioEl(m);
    if (hangup) { try { void m.call.hangup(); } catch { /* ignore */ } }
    if (this._primaryId === m.id) this._primaryId = this._pickPrimary()?.id ?? null;
  }

  /** Per-call audio sink. One element PER CALL is mandatory for concurrency: the SDK's shared
   *  client-level element is last-writer-wins, so a second call would silently steal the first
   *  call's audio (SDK warning 33011). */
  private _createCallAudioEl(): HTMLAudioElement {
    const el = document.createElement('audio');
    el.autoplay = true;
    el.setAttribute('playsinline', 'true');
    el.style.display = 'none';
    document.body.appendChild(el);
    if (this._selectedSpeakerId && typeof (el as any).setSinkId === 'function') {
      void (el as any).setSinkId(this._selectedSpeakerId).catch(() => undefined);
    }
    if (!this._devicechangeBound && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', () => { void this._refreshAudioDevices(); });
      this._devicechangeBound = true;
    }
    return el;
  }

  private _releaseAudioEl(m: ManagedCall): void {
    const el = m.audioEl;
    m.audioEl = null;
    if (!el) return;
    try {
      el.pause();
      (el as any).srcObject = null;
      el.remove();
    } catch { /* ignore */ }
  }

  private _canSetSinkId(): boolean {
    return typeof (HTMLAudioElement.prototype as any).setSinkId === 'function';
  }

  /**
   * A call never started (login/socket error). Surface a short message via state 'ended' (NOT 'idle' —
   * the overlay only renders while state != 'idle', so an 'idle' emit would hide the error).
   */
  private _failCallStart(err: unknown): void {
    this._markDisconnected();
    // A receiving tab must not go deaf over a failed outbound start (review-9 #4): the teardown
    // above leaves no SDK client while the session keeps heartbeating — ring-eligible but
    // unregistered, indefinitely (nothing else reconnects until the ~23h token refresh). Kick the
    // standard receive recovery; it waits out the post-call screen via its own guards.
    if (this._receiveMode) this._scheduleReceiveRetry();
    const detail = err instanceof Error ? err.message : String(err ?? '');
    const canRedial = !!this._lastCall;
    this._emit({
      state: 'ended',
      error: detail
        ? `Couldn't start the call: ${detail}`
        : "Couldn't start the call. Please check your connection and try again.",
      canRedial,
    });
    this._scheduleIdle(canRedial ? WebTelnyxVoiceManager.REDIAL_ENDED_MS : undefined);
  }

  /**
   * A call ended UNCLEANLY (dead socket / network loss): our BYE could not reach Telnyx, so kill the
   * server-side leg over HTTPS. If it dropped BEFORE answer, auto-redial once; a mid-conversation drop
   * offers a one-tap Redial instead.
   */
  private _handleUncleanEnd(errorReason?: string, lostCallCount = 1): void {
    this._clearAllCalls();
    // Still kill any server-side leg — a hang-up before the INVITE landed can leave one ringing.
    void this._serverHangupFallback();
    // The user hung up: this isn't a drop, so never auto-redial it and never offer "Call dropped —
    // Redial?". endCall already ended the UI; just don't undo that.
    if (this._userEnded) return;
    // Never auto-redial out of a multi-call drop: only one of the lost calls could be redialed,
    // and silently dialing one of two callers back is worse than offering nothing.
    if (lostCallCount < 2 && !this._reachedConnected && this._redialCount < 1 && this._lastCall) {
      this._redialCount++;
      this._emit({ ...this._currentState, state: 'connecting', error: undefined });
      this._clearRedialTimer();
      this._redialTimer = setTimeout(() => { void this.redial(false); }, WebTelnyxVoiceManager.REDIAL_DELAY_MS);
      return;
    }
    const canRedial = !!this._lastCall;
    this._emit({
      ...this._currentState,
      state: 'ended',
      isMuted: false,
      error: errorReason ?? (canRedial ? 'Call dropped — connection lost.' : undefined),
      canRedial,
    });
    this._scheduleIdle(canRedial ? WebTelnyxVoiceManager.REDIAL_ENDED_MS : undefined);
  }

  private async _serverHangupFallback(): Promise<void> {
    try {
      await apiService.hangupActiveVoiceCall();
    } catch (err) {
      clientLogger.debug('[TelnyxVoiceWeb] server hangup fallback failed:', err);
    }
  }

  private _startConnectWatchdog(): void {
    this._clearConnectWatchdog();
    this._connectTimer = setTimeout(() => {
      const s = this._currentState.state;
      if (s === 'connecting' || s === 'ringing' || s === 'reconnecting') {
        clientLogger.debug(`[TelnyxVoiceWeb] call stuck in "${s}" — aborting`);
        void this._abortStuckCall();
      }
    }, WebTelnyxVoiceManager.CONNECT_TIMEOUT_MS);
  }

  private _clearConnectWatchdog(): void {
    if (this._connectTimer !== null) { clearTimeout(this._connectTimer); this._connectTimer = null; }
  }

  private async _abortStuckCall(): Promise<void> {
    // Only the stuck (primary) call goes — another line may be perfectly healthy on hold.
    const m = this._primary;
    if (m) this._dropCall(m, false);
    try { await m?.call.hangup(); } catch { /* best effort */ }
    void this._serverHangupFallback();
    if (this._calls.size > 0) { this._emitPrimaryState(); return; }
    const canRedial = !!this._lastCall;
    this._emit({ ...this._currentState, state: 'ended', isMuted: false, error: 'The call could not be connected.', canRedial });
    this._scheduleIdle(canRedial ? WebTelnyxVoiceManager.REDIAL_ENDED_MS : undefined);
  }

  private _scheduleIdle(delayMs: number = 2000): void {
    setTimeout(() => {
      // A call that arrived (or survived) since must never be hidden by the post-call settle.
      if (this._calls.size > 0) return;
      if (this._currentState.state === 'ended') this._emit({ state: 'idle' });
    }, delayMs);
  }

  /** Forget every call without hanging up — for paths where the socket is already gone. */
  private _clearAllCalls(): void {
    for (const m of this._calls.values()) this._releaseAudioEl(m);
    this._calls.clear();
    this._primaryId = null;
  }

  private _clearRedialTimer(): void {
    if (this._redialTimer !== null) { clearTimeout(this._redialTimer); this._redialTimer = null; }
  }

  private async _enumerateOutputs(): Promise<AudioDevice[]> {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      return all
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({ type: outputDeviceType(d.label), name: d.label || 'Audio output', id: d.deviceId }));
    } catch (err) {
      clientLogger.debug('[TelnyxVoiceWeb] enumerateDevices failed:', err);
      return [];
    }
  }

  private async _refreshAudioDevices(): Promise<void> {
    if (!this._canSetSinkId()) return; // no output switching (Safari)
    try {
      const { devices, selected } = await this.getAudioDevices();
      this._emit({ ...this._currentState, availableAudioDevices: devices, audioRoute: selected });
    } catch (err) {
      clientLogger.debug('[TelnyxVoiceWeb] Failed to refresh audio devices:', err);
    }
  }

  private _decodeJwtExpMs(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const exp = JSON.parse(json).exp;
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
      return null;
    }
  }

  private async _fetchToken(): Promise<void> {
    const now = Date.now();
    // RECEIVE MODE: the session token is the SOLE auth — even while the session is momentarily
    // null (enable in flight, heartbeat-404 self-heal): _obtainSessionToken creates one rather
    // than falling through to getVoiceToken(), which would mint a softphone-{userId} credential,
    // reconnect under it, and silently deregister this tab from its session SIP username.
    // Outbound calls placed from a receive-mode tab reuse the session-authenticated client (the
    // parked-outbound TeXML path only cares about the X-Client-State header, not which credential
    // dialed).
    // Cap-blocked exception: this tab CANNOT register (409 — too many receiving tabs), so there is
    // no session credential to protect; fall through to the softphone token so outbound still works.
    if (this._receiveMode && !this._capBlocked) {
      if (!this._session || !this._token || now >= this._tokenExpMs) {
        await this._obtainSessionToken(this._receiveGen);
      }
      return;
    }
    if (!this._token || now >= this._tokenExpMs) {
      // The session-token path above is generation-guarded internally; this softphone path must
      // be too (review-6 #1): a fetch in flight across destroy() would otherwise write the OLD
      // account's token/identity back over the reset fields.
      const ctxGen = this._contextGen;
      const tok = await apiService.getVoiceToken();
      if (ctxGen !== this._contextGen) throw new Error(CONTEXT_CHANGED);
      this._token = tok.token;
      this._identity = tok.identity;
      const serverExpMs = now + Math.max((tok.ttl - 60) * 1000, 30_000);
      const jwtExpMs = this._decodeJwtExpMs(tok.token);
      this._tokenExpMs = jwtExpMs ? Math.min(serverExpMs, jwtExpMs - 60_000) : serverExpMs;
      this._scheduleRefresh(this._tokenExpMs - now);
    }
  }

  private _scheduleRefresh(delayMs: number): void {
    this._clearRefreshTimer();
    this._refreshTimer = setTimeout(() => {
      // RECEIVE MODE: a torn-down client cannot receive calls — refresh the session token and
      // reconnect IMMEDIATELY instead of lazily on the next outbound call. Guard on receive mode
      // ALONE (review fix): with _session momentarily null (self-heal in flight) the old
      // `_receiveMode && _session` condition fell through to _markDisconnected() and tore the
      // client down mid-reheal; _refreshReceiveToken handles the null session by creating one.
      if (this._receiveMode) {
        void this._refreshReceiveToken();
        return;
      }
      // Force a fresh token on the next connect; the next call (or pre-warm) reconnects with it.
      this._token = '';
      this._tokenExpMs = 0;
      if (!this._callActive) this._markDisconnected();
    }, delayMs);
  }

  private _clearRefreshTimer(): void {
    if (this._refreshTimer !== null) { clearTimeout(this._refreshTimer); this._refreshTimer = null; }
  }

  private async _teardownClient(): Promise<void> {
    const client = this._client;
    this._client = null;
    // Calls belong to the client that carried them; a new client never inherits them.
    this._clearAllCalls();
    if (client) {
      try { await client.disconnect(); } catch { /* ignore */ }
    }
  }

  /** Mark the client as needing a reconnect on the next init (e.g. socket closed / token refreshed). */
  private _markDisconnected(): void {
    this._initialized = false;
    void this._teardownClient();
  }

  private async _connectClient(): Promise<void> {
    // Context generation (review-6 #1): destroy() can run during ANY of the awaits below, and
    // this method both reads and writes the shared token/client fields. Every await is followed
    // by a re-check; a client created for a dead context is disconnected, never adopted.
    const ctxGen = this._contextGen;
    // Not connected until proven otherwise (review-7 #5): a reconnect used to leave the previous
    // success's _initialized=true (and, on login failure, the dead client assigned), so
    // receiveStatus kept reporting 'registered' through a failing retry loop.
    this._initialized = false;
    await this._fetchToken();
    if (ctxGen !== this._contextGen) throw new Error(CONTEXT_CHANGED);
    await this._teardownClient(); // fresh client each connect (cheap; avoids reusing a half-dead socket)
    const { TelnyxRTC } = await import('@telnyx/webrtc');
    if (ctxGen !== this._contextGen) throw new Error(CONTEXT_CHANGED);
    // Deliberately NO client-level remoteElement: it is shared by every call and last-writer-wins,
    // so a second call would hijack the first call's audio. Each call gets its own element instead
    // (see _createCallAudioEl), passed to newCall()/answer().
    const client = new TelnyxRTC({ login_token: this._token });
    this._client = client;

    // Only the temporary ready/error handlers are attached during the handshake, so a login failure
    // rejects cleanly without the permanent error handler tearing the client down mid-race.
    try {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
          cleanup();
          reject(new Error('Login timed out.'));
        }, WebTelnyxVoiceManager.LOGIN_TIMEOUT_MS);
        const onReady = () => { cleanup(); resolve(); };
        const onError = (e: any) => { cleanup(); reject(new Error(e?.error?.message || 'Login failed.')); };
        const cleanup = () => {
          clearTimeout(timer);
          client.off('telnyx.ready', onReady);
          client.off('telnyx.error', onError);
        };
        client.on('telnyx.ready', onReady);
        client.on('telnyx.error', onError);
        void client.connect();
      });
    } catch (err) {
      // Login failed/timed out: never leave the dead client assigned — receiveStatus (and init's
      // "already connected" early-return) would treat it as live (review-7 #5).
      if (this._client === client) this._client = null;
      try { void client.disconnect(); } catch { /* ignore */ }
      throw err;
    }

    if (ctxGen !== this._contextGen) {
      // Logged in as the PREVIOUS context (destroy ran mid-handshake): never adopt this client.
      if (this._client === client) this._client = null;
      try { void client.disconnect(); } catch { /* ignore */ }
      throw new Error(CONTEXT_CHANGED);
    }

    this._registerListeners(client);
  }

  private _registerListeners(client: TelnyxRTCClient): void {
    // Call notifications get the same stale-client guard as socket close/error below (review-5):
    // a late callUpdate from a torn-down client (e.g. the ended call's 'ringing'/'active' echo
    // arriving while destroy() drains) would otherwise re-adopt the dead call and resurrect a
    // live call state that nothing can ever clear again.
    client.on('telnyx.notification', (n: INotification) => {
      if (this._client !== client) return;
      this._onNotification(n);
    });
    // Events from a client that is no longer THE client are intentional-teardown echoes
    // (_teardownClient nulls _client BEFORE disconnect): reacting to them turned every planned
    // reconnect into a spurious "dead client" retry loop with deaf windows (review-4 fix).
    //
    // A socket drop while a call is live means our BYE can't reach Telnyx — treat as an unclean end.
    // While idle: receive mode must actively reconnect (a torn-down client receives nothing);
    // otherwise the next outbound call just reconnects lazily.
    client.on('telnyx.socket.close', () => {
      if (this._client !== client) return;
      this._onClientDead();
    });
    // Known call-scoped errors are NOT client death (review-9 #3, closing TODO(benign-call-errors)
    // for the known case): hanging up an already-gone call yields JSON-RPC -32002 "CALL DOES NOT
    // EXIST" — e.g. auto-rejecting a second invite whose caller just cancelled, or ending a call
    // before its INVITE landed. Treating that as a dead client tore down the FIRST (live) call's
    // UI and socket. Classification is still blocklist-based: anything not known-benign keeps the
    // full teardown path, because missing a real socket/auth failure would leave a deaf client.
    client.on('telnyx.error', (e: any) => {
      if (this._client !== client) return;
      const code = e?.error?.code;
      const msg: string = e?.error?.message ?? '';
      if (code === -32002 || /call does not exist/i.test(msg)) {
        clientLogger.debug('[TelnyxVoiceWeb] ignoring call-scoped SDK error:', msg || code);
        return;
      }
      this._onClientDead(e?.error?.message);
    });
  }

  private _onClientDead(errorReason?: string): void {
    // The socket carried every call on this tab, so they all die together.
    const calls = [...this._calls.values()];
    const lostLive = calls.filter((m) => m.role !== 'incoming').length;
    const hadRinging = calls.some((m) => m.role === 'incoming');
    const wasLive = lostLive > 0 || this._callActiveByState;
    this._clearAllCalls(); // no hangups — the legs are gone with the socket
    if (wasLive) {
      this._handleUncleanEnd(errorReason, lostLive);
      if (this._receiveMode) this._scheduleReceiveRetry();
      return;
    }
    // Nothing but ringing invites: clear the ring UI, no "call dropped" screen for a call that
    // never started.
    if (hadRinging && this._currentState.state === 'incoming') this._emit({ state: 'idle' });
    this._markDisconnected();
    if (this._receiveMode) this._scheduleReceiveRetry();
  }

  private _onNotification(n: INotification): void {
    if (n.type !== 'callUpdate' || !n.call) return;
    const call = n.call;
    const callState = call.state as string;
    const terminal = callState === 'hangup' || callState === 'destroy' || callState === 'purge';

    let managed = this._calls.get(call.id) ?? this._rekeyRecoveredCall(call);

    if (!managed) {
      // Terminal echoes must never be ADOPTED (review-6 #4): after a call is cleared (rejected /
      // caller hung up), the SDK can still emit destroy/purge for it — without this guard the dead
      // call would be adopted as a NEW ringing invite: ghost modal + ringtone for a caller who is
      // long gone, stuck until manually declined (no further events ever arrive for a terminal call).
      if (terminal) return;
      if ((call.direction as string) === 'inbound') {
        this._onInboundInvite(call);
        return;
      }
      // Adopt an unknown call ONLY while WE are placing one (live-testing fix): the SDK's Call
      // constructor dispatches a state-'new' notification for EVERY call — including an incoming
      // invite, whose `direction` is only assigned AFTER construction (bundle v2.27.4:
      // `const e = C(); e.direction = Inbound; e.setState(Ringing)`). Unconditional adoption here
      // turned invites into phantom OUTBOUND calls: the follow-up 'ringing' event (direction set
      // by then) matched the adopted call, skipped the inbound branch, and the tab showed an
      // outgoing-call widget with no way to answer. An unknown call while no dial is in flight is
      // a not-yet-labeled invite — leave it alone; its 'ringing' notification rings properly.
      if (!this._dialInFlight) return;
      managed = this._adoptOutbound(call);
    }

    // A ringing invite only has one transition worth acting on before it is answered: it ending
    // (caller hung up, or another tab / the fallback phone won the race).
    if (managed.role === 'incoming') {
      if (terminal) this._onCallEnded(managed);
      return;
    }

    switch (callState) {
      case 'new':
      case 'requesting':
      case 'trying':
      case 'answering':
      case 'ringing':
      case 'early':
      case 'recovering':
        this._emitPrimaryState();
        if (callState === 'ringing' || callState === 'early') void this._refreshAudioDevices();
        break;
      case 'active':
        if (!managed.connectedAt) managed.connectedAt = Date.now();
        if (managed.intendedHeld) {
          // The user holds this call but the server says active — a socket reattach silently
          // resurrects held calls as active (the SDK carries no hold state across recovery), and
          // the recovered call has a FRESH mic stream, so the local layer must be re-applied too.
          this._localHold(managed);
          managed.role = 'held';
          if (!managed.reasserting) {
            managed.reasserting = true;
            void this._serverHold(managed, true).finally(() => { managed.reasserting = false; });
          }
        } else {
          managed.role = 'active';
          if (managed.localHold) this._localResume(managed);
        }
        this._emitPrimaryState();
        void this._refreshAudioDevices();
        break;
      case 'held':
        // The server confirmed the hold (ours, or one it initiated) — keep the local layer and the
        // roster in step with it.
        managed.role = 'held';
        if (!managed.localHold) this._localHold(managed);
        this._emitPrimaryState();
        break;
      case 'hangup':
      case 'destroy':
      case 'purge':
        this._onCallEnded(managed);
        break;
      default:
        break;
    }
  }

  /**
   * A socket reattach does NOT reuse the Call object: the SDK builds a fresh one with a NEW id and
   * the old id in `recoveredCallId` (verified in bundle v2.27.4). Without re-keying, the recovered
   * call would look like an unknown inbound call — i.e. it would ring the user for the call they
   * are already on — and the original entry would linger forever.
   */
  private _rekeyRecoveredCall(call: TelnyxCall): ManagedCall | undefined {
    const previousId = (call as any).recoveredCallId as string | undefined;
    if (!previousId) return undefined;
    const m = this._calls.get(previousId);
    if (!m) return undefined;
    this._calls.delete(previousId);
    m.id = call.id;
    m.call = call;
    this._calls.set(call.id, m);
    if (this._primaryId === previousId) this._primaryId = call.id;
    return m;
  }

  /**
   * An inbound INVITE arrived. It rings when receive mode is on and this tab has room for it —
   * with a call already up that is CALL WAITING (the second line), which is the whole point of
   * this feature. 'ended' counts as free (review fix): the post-call "Call ended"/redial screen
   * lingers ~2-10s, and a receptionist must be reachable immediately after hanging up.
   * When it can't be taken, THIS tab's leg is rejected — the caller keeps ringing the other Dial
   * legs (other tabs, the fallback phone), exactly as before (Phase 0 spike).
   */
  private _onInboundInvite(call: TelnyxCall): void {
    const reject = (why: string): void => {
      clientLogger.debug(`[TelnyxVoiceWeb] rejecting invite: ${why}`);
      try { void call.hangup(); } catch { /* ignore */ }
    };
    if (!this._receiveMode) { reject('receive mode is off'); return; }
    if (this._calls.size >= MAX_CONCURRENT_CALLS) { reject('concurrent call limit reached'); return; }
    // A call may ring even while another is still RINGING or connecting — you don't have to wait for
    // the first to be answered before a second can come through. The only limit is UI slots: two
    // rings can be shown at once (the full modal for the primary, one banner for a waiting call), so
    // refuse a THIRD ring — i.e. when a non-primary ring is already waiting. Answering while another
    // call is merely ringing just doesn't auto-hold it (there's nothing established to hold);
    // answering while a call is active/held holds that one first, as before.
    const waitingRing = [...this._calls.values()].some(
      (c) => c.role === 'incoming' && c.id !== this._primaryId);
    if (waitingRing) { reject('a second call is already waiting'); return; }
    // Outbound mid-dial has emitted 'connecting' but has no SDK call object yet (size 0): adopting
    // an invite in that sliver would race the outbound's own adoption over _primaryId. Once the
    // outbound has a call object (ringing), the roster guards above take over and invites are fine.
    if (this._calls.size === 0) {
      const state = this._currentState.state;
      if (state !== 'idle' && state !== 'ended') { reject(`tab is busy (${state})`); return; }
    }

    this._clearRedialTimer(); // an auto-redial must not fire under the incoming ring
    const m: ManagedCall = {
      id: call.id,
      call,
      direction: 'inbound',
      role: 'incoming',
      audioEl: null, // created when answered — no sink is needed while it only rings
      intendedHeld: false,
      localHold: false,
      isMuted: false,
      toE164: (call as any).options?.remoteCallerNumber ?? (call as any).remoteCallerNumber ?? '',
      incomingContext: readIncomingContext(call),
    };
    this._calls.set(m.id, m);
    // The FIRST ring owns the screen (full modal + ringtone, state 'incoming'). A second one is a
    // waiting call: the primary keeps the widget, and the invite shows up only in the roster —
    // which is what renders the compact banner and its softer call-waiting beep.
    if (this._calls.size === 1) this._primaryId = m.id;
    this._emitPrimaryState();
  }

  private _onCallEnded(m: ManagedCall): void {
    const wasRinging = m.role === 'incoming';
    const call = m.call;
    this._dropCall(m, false);
    // Another line is still up (the classic case: the second caller hangs up while the first waits
    // on hold). Keep the widget on the survivor — deliberately still held, never auto-resumed.
    if (this._calls.size > 0) { this._emitPrimaryState(); return; }
    if (!this._callActive) return; // already ended/idle
    if (wasRinging) {
      // A ring that ended before it was answered is not a call that "ended" — no post-call screen.
      if (this._currentState.state === 'incoming') this._emit({ state: 'idle' });
      return;
    }
    // A hangup/destroy notification means a BYE was exchanged — a clean end. Surface an abnormal SIP
    // cause as an error, but no redial (the leg is gone cleanly).
    const abnormal = call.causeCode && call.causeCode !== 16; // 16 = NORMAL_CLEARING
    this._emit({ state: 'ended', callSid: call.id, error: abnormal ? call.cause : undefined });
    this._scheduleIdle();
  }
}

export const telnyxVoiceWebManager = WebTelnyxVoiceManager.instance;
